import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { SalesLayout } from '../components/SalesLayout';
import { useNavigate, useLocation } from 'react-router-dom';
import { Pagination } from '../components/Pagination';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Get date in YYYY-MM-DD format
const formatDateValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayDate = () => formatDateValue(new Date());

const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return formatDateValue(tomorrow);
};

const getYesterdayDate = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return formatDateValue(yesterday);
};

function DeliveryReport({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = React.useState([]);
  const [filteredData, setFilteredData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [dateFrom, setDateFrom] = React.useState(getTodayDate());
  const [dateTo, setDateTo] = React.useState(getTodayDate());
  const [generatingLink, setGeneratingLink] = React.useState(false);
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [sharePhone, setSharePhone] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 10;

  React.useEffect(() => {
    fetchBookings();
  }, []);

  React.useEffect(() => {
    applyFilter();
  }, [bookings, dateFrom, dateTo]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings?limit=10000`);
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    }
    setLoading(false);
  };

  const applyFilter = () => {
    let filtered = bookings.filter(b => {
      if (!b.delivery_date) return false;
      const deliveryDate = new Date(b.delivery_date);
      const from = new Date(dateFrom);
      const to = new Date(dateTo);
      to.setHours(23, 59, 59);
      return deliveryDate >= from && deliveryDate <= to;
    });
    setFilteredData(filtered);
    setCurrentPage(1); // Reset to page 1 when filter changes
  };

  // Pagination
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Quick date setters
  const setToday = () => {
    setDateFrom(getTodayDate());
    setDateTo(getTodayDate());
  };

  const setTomorrow = () => {
    setDateFrom(getTomorrowDate());
    setDateTo(getTomorrowDate());
  };

  const setPrevious = () => {
    setDateFrom(getYesterdayDate());
    setDateTo(getYesterdayDate());
  };

  // Group by Sales Representative - using paginated data
  const groupedData = React.useMemo(() => {
    const groups = {};
    paginatedData.forEach(booking => {
      const salesRep = booking.sales_representative || 'Unassigned';
      if (!groups[salesRep]) {
        groups[salesRep] = [];
      }
      groups[salesRep].push(booking);
    });
    return groups;
  }, [paginatedData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN');
  };

  // Export to Excel
  const handleExportExcel = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/reports/delivery/export`, {
        date_from: dateFrom,
        date_to: dateTo,
        format: 'excel'
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Delivery_Report_${dateFrom}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel downloaded');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  // Export to PDF
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      doc.setFontSize(16);
      doc.text('Daily Vehicles Ready for Delivery', 14, 15);
      doc.setFontSize(10);
      doc.text(`Date: ${formatDate(dateFrom)} to ${formatDate(dateTo)}`, 14, 22);
      doc.text(`Total Vehicles: ${filteredData.length}`, 14, 28);

      let yPos = 35;
      
      Object.entries(groupedData).forEach(([salesRep, items]) => {
        if (yPos > 180) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(`Sales Rep: ${salesRep} (${items.length} vehicles)`, 14, yPos);
        doc.setFont(undefined, 'normal');
        yPos += 5;

        const tableData = items.map((b, idx) => [
          idx + 1,
          b.booking_no || '-',
          `${b.customer_first_name || ''} ${b.customer_last_name || ''}`.trim() || '-',
          b.mobile_no_1 || '-',
          `${b.model || ''} ${b.variant || ''} ${b.color || ''}`.trim() || '-',
          b.chassis_no || '-',
          b.engine_motor_no || '-',
          b.battery_no || '-',
          b.controller_no || '-',
          b.registration_number || '-',
          b.customer_balance || '-',
          b.pme_drive_no || '-'
        ]);

        doc.autoTable({
          startY: yPos,
          head: [['#', 'Booking', 'Customer', 'Mobile', 'Vehicle', 'Chassis', 'Engine/Motor', 'Battery', 'Controller', 'Reg No', 'Balance', 'PME']],
          body: tableData,
          styles: { fontSize: 7, cellPadding: 1 },
          headStyles: { fillColor: [59, 130, 246], fontSize: 7 },
          columnStyles: {
            9: { fontStyle: 'bold' } // Registration No in bold
          },
          margin: { left: 14, right: 14 }
        });
        
        yPos = doc.lastAutoTable.finalY + 10;
      });

      doc.save(`Delivery_Report_${dateFrom}.pdf`);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  // Export to Word (HTML format that opens in Word)
  const handleExportWord = () => {
    let html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
      <head><meta charset="utf-8"><title>Delivery Report</title></head>
      <body style="font-family: Arial;">
      <h1>Daily Vehicles Ready for Delivery</h1>
      <p>Date: ${formatDate(dateFrom)} to ${formatDate(dateTo)} | Total: ${filteredData.length} vehicles</p>
    `;

    Object.entries(groupedData).forEach(([salesRep, items]) => {
      html += `<h3 style="color: #2563eb;">Sales Rep: ${salesRep} (${items.length} vehicles)</h3>`;
      html += `<table border="1" cellpadding="4" style="border-collapse: collapse; font-size: 10px;">`;
      html += `<tr style="background: #3b82f6; color: white;">
        <th>#</th><th>Booking</th><th>Customer</th><th>Mobile</th><th>Vehicle</th>
        <th>Chassis</th><th>Engine/Motor</th><th>Battery</th><th>Controller</th>
        <th>Reg No</th><th>Customer Balance</th><th>PME Drive</th>
      </tr>`;
      
      items.forEach((b, idx) => {
        html += `<tr>
          <td>${idx + 1}</td>
          <td>${b.booking_no || '-'}</td>
          <td>${b.customer_first_name || ''} ${b.customer_last_name || ''}</td>
          <td>${b.mobile_no_1 || '-'}</td>
          <td>${b.model || ''} ${b.variant || ''} ${b.color || ''}</td>
          <td>${b.chassis_no || '-'}</td>
          <td>${b.engine_motor_no || '-'}</td>
          <td>${b.battery_no || '-'}</td>
          <td>${b.controller_no || '-'}</td>
          <td><b>${b.registration_number || '-'}</b></td>
          <td>${b.customer_balance || '-'}</td>
          <td>${b.pme_drive_no || '-'}</td>
        </tr>`;
      });
      html += `</table><br/>`;
    });

    html += `</body></html>`;
    
    const blob = new Blob([html], { type: 'application/msword' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Delivery_Report_${dateFrom}.doc`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Word document downloaded');
  };

  // Print
  const handlePrint = () => {
    const printContent = document.getElementById('report-content');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
      <head>
        <title>Delivery Report - ${formatDate(dateFrom)}</title>
        <style>
          body { font-family: Arial; font-size: 10px; margin: 10px; }
          table { border-collapse: collapse; width: 100%; margin-bottom: 15px; }
          th, td { border: 1px solid #ddd; padding: 4px; text-align: left; }
          th { background: #3b82f6; color: white; }
          h1 { font-size: 16px; margin-bottom: 5px; }
          h3 { font-size: 12px; color: #2563eb; margin: 10px 0 5px 0; }
          .bold { font-weight: bold; }
          @media print { body { -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <h1>Daily Vehicles Ready for Delivery</h1>
        <p>Date: ${formatDate(dateFrom)} to ${formatDate(dateTo)} | Total: ${filteredData.length} vehicles</p>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  // WhatsApp share with link
  const handleWhatsAppShare = async () => {
    try {
      setGeneratingLink(true);
      const response = await axios.post(`${BACKEND_URL}/api/reports/delivery/share-link`, {
        date_from: dateFrom,
        date_to: dateTo
      });
      
      const shareLink = response.data.share_link;
      const text = encodeURIComponent(
        `🚗 *Daily Delivery Report*\n` +
        `📅 Date: ${formatDate(dateFrom)} to ${formatDate(dateTo)}\n` +
        `📊 Total Vehicles: ${filteredData.length}\n\n` +
        `📥 Download Report:\n${shareLink}\n\n` +
        `(Link expires in 24 hours)`
      );
      
      const url = sharePhone ? `https://web.whatsapp.com/send?phone=${sharePhone}&text=${text}` : `https://web.whatsapp.com/send?text=${text}`;
      { const _a = document.createElement('a'); _a.href = url; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
      toast.success('WhatsApp opened');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Failed to generate share link');
    } finally {
      setGeneratingLink(false);
    }
  };

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Delivery Report">
      <div className="p-6 max-w-full">
        {/* Header - Pastel color */}
        <div className="bg-gradient-to-r from-emerald-400 to-teal-400 rounded-xl p-6 text-white mb-6">
          <h1 className="text-2xl font-bold mb-2">🚗 Daily Vehicles Ready for Delivery</h1>
          <p className="opacity-90">Grouped by Sales Representative</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="p-2 border rounded-lg text-sm"
                data-testid="date-from-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="p-2 border rounded-lg text-sm"
                data-testid="date-to-input"
              />
            </div>
            
            {/* Quick Date Buttons */}
            <div className="flex gap-2">
              <button
                onClick={setToday}
                className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 text-sm font-medium"
                data-testid="today-btn"
              >
                Today
              </button>
              <button
                onClick={setTomorrow}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
                data-testid="tomorrow-btn"
              >
                Tomorrow
              </button>
              <button
                onClick={setPrevious}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm font-medium"
                data-testid="previous-btn"
              >
                Previous
              </button>
            </div>
            
            <div className="flex-1" />
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
              data-testid="print-btn"
            >
              🖨️ Print
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              data-testid="export-btn"
            >
              📤 Export / Share
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-green-50 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-green-600">{filteredData.length}</div>
            <div className="text-sm text-green-700">Total Vehicles</div>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-blue-600">{Object.keys(groupedData).length}</div>
            <div className="text-sm text-blue-700">Sales Reps</div>
          </div>
        </div>

        {/* Pagination - Top Right */}
        <div className="flex justify-end mb-4">
          <Pagination
            currentPage={currentPage}
            totalItems={filteredData.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            showInfo={true}
          />
        </div>

        {/* Report Content */}
        <div id="report-content" className="space-y-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : Object.keys(groupedData).length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center text-gray-500">
              No vehicles scheduled for delivery on selected date(s)
            </div>
          ) : (
            Object.entries(groupedData).map(([salesRep, items]) => (
              <div key={salesRep} className="bg-white rounded-xl border shadow-sm overflow-hidden">
                <div className="bg-teal-400 text-white px-4 py-3">
                  <h3 className="font-semibold">Sales Rep: {salesRep} ({items.length} vehicles)</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="delivery-table">
                    <thead className="bg-teal-50">
                      <tr>
                        <th className="p-2 text-left">#</th>
                        <th className="p-2 text-left">Booking No</th>
                        <th className="p-2 text-left">Customer Name</th>
                        <th className="p-2 text-left">Mobile</th>
                        <th className="p-2 text-left">Model, Variant, Color</th>
                        <th className="p-2 text-left">Chassis No</th>
                        <th className="p-2 text-left">Engine/Motor No</th>
                        <th className="p-2 text-left">Battery No</th>
                        <th className="p-2 text-left">Controller No</th>
                        <th className="p-2 text-left font-bold">Registration No</th>
                        <th className="p-2 text-left">Customer Balance</th>
                        <th className="p-2 text-left">PME Drive No</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((b, idx) => (
                        <tr key={b.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-2">{idx + 1}</td>
                          <td className="p-2">{b.booking_no}</td>
                          <td className="p-2">{b.customer_first_name} {b.customer_last_name}</td>
                          <td className="p-2">{b.mobile_no_1}</td>
                          <td className="p-2">{b.model} {b.variant} {b.color}</td>
                          <td className="p-2 font-mono">{b.chassis_no || '-'}</td>
                          <td className="p-2 font-mono">{b.engine_motor_no || '-'}</td>
                          <td className="p-2">{b.battery_no || '-'}</td>
                          <td className="p-2">{b.controller_no || '-'}</td>
                          <td className="p-2 font-bold text-blue-700">{b.registration_number || '-'}</td>
                          <td className="p-2">{b.customer_balance || '-'}</td>
                          <td className="p-2">{b.pme_drive_no || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Export Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
              <h3 className="text-lg font-bold mb-4">Export & Share</h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={handleExportExcel} className="p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                    📊 Excel
                  </button>
                  <button onClick={handleExportPDF} className="p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                    📄 PDF
                  </button>
                  <button onClick={handleExportWord} className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                    📝 Word
                  </button>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Share via WhatsApp</p>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={sharePhone}
                      onChange={(e) => setSharePhone(e.target.value)}
                      className="flex-1 p-2 border rounded-lg text-sm"
                    />
                    <button
                      onClick={handleWhatsAppShare}
                      disabled={generatingLink}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm disabled:bg-green-400"
                    >
                      {generatingLink ? 'Generating...' : 'WhatsApp'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Includes download link (24hr expiry)</p>
                </div>
              </div>

              <button
                onClick={() => setShowExportModal(false)}
                className="w-full mt-4 px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </SalesLayout>
  );
}

export default DeliveryReport;
